import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Save, Trash2, UserPlus } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { PageHeader } from "@/components/PageHeader";
import { MembersApi, ProjectsApi } from "@/lib/api";
import type { TagDefinition } from "@/lib/types";

export function ProjectSettingsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const pid = Number(projectId);
  const qc = useQueryClient();
  const navigate = useNavigate();

  const project = useQuery({
    queryKey: ["project", pid],
    queryFn: () => ProjectsApi.get(pid),
    enabled: Number.isFinite(pid),
  });

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [bucket, setBucket] = useState("");
  const [prefix, setPrefix] = useState("");
  const [tags, setTags] = useState<TagDefinition[]>([]);

  useEffect(() => {
    if (project.data) {
      setName(project.data.name);
      setDescription(project.data.description ?? "");
      setBucket(project.data.s3_bucket);
      setPrefix(project.data.s3_prefix);
      setTags(project.data.tag_schema);
    }
  }, [project.data]);

  const save = useMutation({
    mutationFn: () =>
      ProjectsApi.update(pid, {
        name,
        description: description || null,
        s3_bucket: bucket,
        s3_prefix: prefix,
        tag_schema: tags,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project", pid] }),
  });

  const scan = useMutation({
    mutationFn: () => ProjectsApi.scan(pid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tasks", pid] }),
  });

  const remove = useMutation({
    mutationFn: () => ProjectsApi.delete(pid),
    onSuccess: () => navigate("/projects", { replace: true }),
  });

  const addAll = useMutation({
    mutationFn: () => MembersApi.addAll(pid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["members", pid] }),
  });

  if (project.isLoading) return <div className="p-8 text-sm text-text-muted">Loading…</div>;
  if (project.isError) return <div className="p-8 text-sm text-status-error">Failed to load project.</div>;

  return (
    <>
      <PageHeader
        title="Project settings"
        subtitle={project.data?.name}
        actions={
          <>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => scan.mutate()}
              disabled={scan.isPending}
              title="Scan S3 prefix for new audio files and create tasks"
            >
              {scan.isPending ? "Scanning…" : "Scan S3 prefix"}
            </button>
            <button type="button" className="btn-primary" onClick={() => save.mutate()} disabled={save.isPending}>
              <Save size={16} />
              {save.isPending ? "Saving…" : "Save changes"}
            </button>
          </>
        }
      />

      <div className="max-w-3xl space-y-6 px-4 py-4 md:px-8 md:py-6">
        {scan.data && (
          <div className="rounded-md border border-accent/40 bg-accent/10 px-3 py-2 text-sm text-accent">
            Scan complete — {scan.data.tasks_created} new tasks created.
          </div>
        )}

        <Section title="General">
          <Field label="Name">
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Description">
            <textarea
              className="input min-h-[60px] resize-y"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Field>
        </Section>

        <Section title="S3 location">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Bucket">
              <input className="input font-mono text-xs" value={bucket} onChange={(e) => setBucket(e.target.value)} />
            </Field>
            <Field label="Prefix">
              <input className="input font-mono text-xs" value={prefix} onChange={(e) => setPrefix(e.target.value)} />
            </Field>
          </div>
          <p className="text-xs text-text-dim">
            Use <span className="text-text">Scan S3 prefix</span> after changes to create tasks for new audio files.
          </p>
        </Section>

        <Section title="Members">
          <p className="text-xs text-text-dim">
            Bulk-add every active user as a project contributor. Already-members are skipped.
          </p>
          <button
            type="button"
            className="btn-ghost"
            onClick={() => {
              if (window.confirm("Add all active users as members of this project?")) {
                addAll.mutate();
              }
            }}
            disabled={addAll.isPending}
          >
            <UserPlus size={16} />
            {addAll.isPending ? "Adding…" : "Add all users as members"}
          </button>
          {addAll.data && (
            <div className="rounded-md border border-accent/40 bg-accent/10 px-3 py-2 text-sm text-accent">
              {addAll.data.added === 0
                ? "No new users to add — everyone is already a member."
                : `Added ${addAll.data.added} user${addAll.data.added === 1 ? "" : "s"}.`}
            </div>
          )}
        </Section>

        <Section title="Tag schema">
          <div className="space-y-2">
            {tags.map((tag, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  type="color"
                  className="h-9 w-12 cursor-pointer rounded border border-border bg-bg p-0"
                  value={tag.color}
                  onChange={(e) => updateTag(i, { ...tag, color: e.target.value })}
                />
                <input
                  className="input flex-1"
                  placeholder="Label"
                  value={tag.name}
                  onChange={(e) => updateTag(i, { ...tag, name: e.target.value })}
                />
                <input
                  className="input w-24"
                  placeholder="Shortcut"
                  value={tag.shortcut ?? ""}
                  onChange={(e) => updateTag(i, { ...tag, shortcut: e.target.value || null })}
                  maxLength={1}
                />
                <button
                  type="button"
                  className="rounded-md p-2 text-text-dim hover:bg-bg-elevated hover:text-status-error"
                  onClick={() => setTags(tags.filter((_, j) => j !== i))}
                  title="Remove tag"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
            <button
              type="button"
              className="btn-ghost text-sm"
              onClick={() => setTags([...tags, { name: "", color: "#22d3ee", shortcut: null }])}
            >
              <Plus size={14} />
              Add tag
            </button>
          </div>
        </Section>

        <Section title="Danger zone">
          <button
            type="button"
            className="btn-danger"
            onClick={() => {
              if (window.confirm("Delete this project? Tasks and taggings will be removed.")) remove.mutate();
            }}
          >
            <Trash2 size={16} />
            Delete project
          </button>
        </Section>
      </div>
    </>
  );

  function updateTag(i: number, t: TagDefinition) {
    const next = [...tags];
    next[i] = t;
    setTags(next);
  }
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card space-y-3 p-5">
      <h3 className="text-sm font-semibold text-text">{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-text-muted">{label}</label>
      {children}
    </div>
  );
}
